import React, {useCallback, useEffect, useMemo, useState} from 'react'
import useFetch from './useFetch'
import {AsideMenuItemWithSub} from '../../_metronic/layout/components/aside/AsideMenuItemWithSub'
import {AsideMenuItem} from '../../_metronic/layout/components/aside/AsideMenuItem'
import {useSelector} from 'react-redux'
import {Avatar, Skeleton, Typography} from '@mui/material'
import clsx from 'clsx'
import {Box} from '@mui/system'
import {Grid} from 'ag-grid-community'

interface Modulo {
  id: number
  nombre: string
  estado: number
  padre: number
  ruta: string
  texto: string
}

type UseModuleStructure = () => {structure?: any; loading: boolean; renderModules: any}

const SECTION_MAP = [
  {
    title: 'Menú',
    module: 'menu',
  },
  {
    title: 'Develop',
    module: 'devolop',
  },
]
const ICON_DEFAULT = 'bi-bar-chart'
const ICONS_MAP: any = {
  contratos: {
    icon: 'bi-list-ul',
  },
  minutas: {
    icon: 'bi-card-list',
  },
}

const useModuleStructure: UseModuleStructure = () => {
  const [structure, setStructure] = useState<any>([])
  const [restricted, setRestricted] = useState<any>({})

  const {data: dataModulos, loading} = useFetch('amd_getModulos', {
    pollInterval: true,
  })
  const {data: dataModulosRole} = useFetch('amd_getRoleModulos', {
    pollInterval: true,
  })
  const {user} = useSelector(({auth}: any) => auth)

  const getRestricted = useCallback(() => {
    const obj: any = {}
    const modulosArray = dataModulosRole.map(({nombreModulo}: any) => nombreModulo)
    modulosArray.forEach((item: string) => {
      obj[item] = dataModulosRole
        .filter((modulo: any) => modulo.nombreModulo === item)
        .map(({nombreRole, nombreModulo}: any) => nombreModulo === item && nombreRole)
    })
    setRestricted(obj)
  }, [dataModulosRole])

  const dataStructure = useCallback(() => {
    const orderAsc = (a: any, b: any) => a.texto.toLowerCase() < b.texto.toLowerCase() && -1
    const onlyParents = ({padre}: {padre: number}) => padre === 0

    return dataModulos
      .filter(onlyParents)
      .sort(orderAsc)
      .map((modulo: Modulo) => {
        const onlyFathersChildren = ({padre}: {padre: number}) => padre === modulo.id
        const subModulos = dataModulos
          .filter(onlyFathersChildren)
          .sort(orderAsc)
          .map((submodulo: Modulo) => submodulo)
        return {
          ...modulo,
          subModulos,
          icon: ICONS_MAP[modulo.nombre]?.icon || ICON_DEFAULT,
          fontIcon: 'bi-archive',
        }
      })
  }, [dataModulos])

  const renderSkeleton = () => (
    <div className={clsx('menu-item', 'menu-accordion')}>
      <div className='px-5 my-2'>
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <Box sx={{margin: 1}}>
            <Skeleton variant='circular'>
              <Avatar />
            </Skeleton>
          </Box>
          <Box sx={{width: '100%'}}>
            <Skeleton width='90%'>
              <Typography>.</Typography>
            </Skeleton>
          </Box>
        </Box>
      </div>
      <div className='px-5 my-2'>
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <Box sx={{margin: 1}}>
            <Skeleton variant='circular'>
              <Avatar />
            </Skeleton>
          </Box>
          <Box sx={{width: '100%'}}>
            <Skeleton width='90%'>
              <Typography>.</Typography>
            </Skeleton>
          </Box>
        </Box>
      </div>
      <div className='px-5 my-2'>
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <Box sx={{margin: 1}}>
            <Skeleton variant='circular'>
              <Avatar />
            </Skeleton>
          </Box>
          <Box sx={{width: '100%'}}>
            <Skeleton width='90%'>
              <Typography>.</Typography>
            </Skeleton>
          </Box>
        </Box>
      </div>
      <div className='px-5 my-2'>
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <Box sx={{margin: 1}}>
            <Skeleton variant='circular'>
              <Avatar />
            </Skeleton>
          </Box>
          <Box sx={{width: '100%'}}>
            <Skeleton width='90%'>
              <Typography>.</Typography>
            </Skeleton>
          </Box>
        </Box>
      </div>
      <div className='px-5 my-2'>
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <Box sx={{margin: 1}}>
            <Skeleton variant='circular'>
              <Avatar />
            </Skeleton>
          </Box>
          <Box sx={{width: '100%'}}>
            <Skeleton width='90%'>
              <Typography>.</Typography>
            </Skeleton>
          </Box>
        </Box>
      </div>
    </div>
  )

  const renderModules = () => {
    if (loading && structure.length === 0) {
      return renderSkeleton()
    }
    return structure.map((modulo: any) => {
      if (
        user.roleName === 'admin' ||
        (restricted[modulo.nombre] && restricted[modulo.nombre].includes(user.roleName))
      ) {
        return (
          <AsideMenuItemWithSub
            key={modulo.id}
            to={modulo.ruta}
            title={modulo.texto}
            icon={modulo.icon}
            haveChildren={modulo.subModulos.length}
          >
            {modulo.subModulos.map((submodulo: any, index: number) => (
              <AsideMenuItem
                key={index}
                to={submodulo.ruta}
                title={submodulo.texto}
                hasBullet={true}
              />
            ))}
          </AsideMenuItemWithSub>
        )
      }
    })
  }

  useEffect(() => {
    getRestricted()
  }, [dataModulosRole])

  useEffect(() => {
    setStructure(dataStructure())
  }, [dataModulos])

  return {structure, renderModules, loading}
}

export default useModuleStructure
